/**
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO | SELO: G2GENTE-PROTECTED-2026-02-24-V1
 */
import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, User, Lock, Eye, EyeOff } from 'lucide-react';

// CPF formatting helper
const formatCPF = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

// Detect if input looks like CPF (only digits and CPF formatting chars)
const looksLikeCPF = (value) => {
  const cleaned = value.replace(/[\s.-]/g, '');
  return /^\d+$/.test(cleaned) && cleaned.length <= 11;
};

export default function MobileLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ identifier: '', password: '' });
  
  // Redirect URL após login (ex: vindo de convite de teste)
  const redirectTo = searchParams.get('redirect');

  const handleIdentifierChange = (e) => {
    let value = e.target.value;
    
    // Auto-format if it looks like a CPF
    if (looksLikeCPF(value)) {
      value = formatCPF(value);
    }
    
    setForm({ ...form, identifier: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.identifier, form.password);
      
      // Check if user needs to change password (forced by admin)
      if (user.force_password_change) {
        toast.warning('Você precisa criar uma nova senha para continuar', { duration: 5000 });
        navigate('/m/change-password');
        return;
      }
      
      // Check if user needs to update CPF
      if (user.needs_cpf_update) {
        toast.warning('Por favor, atualize seu cadastro com seu CPF', { duration: 5000 });
      }
      
      // Redirect based on role or to custom redirect URL
      const targetUrl = redirectTo || '/m';
      
      if (user.role === 'CANDIDATE') {
        toast.success('Login realizado!');
        navigate(targetUrl);
      } else if (user.role === 'EMPLOYEE') {
        toast.success('Bem-vindo(a)!');
        navigate(redirectTo || '/m/employee');
      } else if (user.role === 'COMPANY' || user.role === 'ADMIN' || user.role === 'PARTNER') {
        // Gestores/Admin acessando o app pessoal - mostrar aviso importante
        toast('Este app é de uso pessoal. Utilize apenas seus dados e contatos pessoais aqui.', {
          icon: '👤',
          duration: 6000,
          style: {
            background: '#0891b2',
            color: '#fff',
          }
        });
        setTimeout(() => {
          toast.success('Bem-vindo(a) ao app!');
        }, 500);
        navigate(targetUrl);
      } else {
        toast.success('Login realizado!');
        navigate(targetUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex flex-col">
      {/* Header */}
      <div className="p-4">
        <button onClick={() => navigate('/')} className="text-slate-400">
          <ArrowLeft className="w-6 h-6" />
        </button>
      </div>

      {/* Logo */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 bg-cyan-500 rounded-2xl flex items-center justify-center mb-4">
          <span className="text-white font-bold text-3xl">G2</span>
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">G2eGente</h1>
        <p className="text-slate-400 text-center mb-8">Sua próxima oportunidade te espera</p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">CPF ou Email</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
              <Input
                type="text"
                placeholder="000.000.000-00 ou email"
                value={form.identifier}
                onChange={handleIdentifierChange}
                className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                required
                data-testid="mobile-login-identifier"
              />
            </div>
            <p className="text-xs text-slate-500">
              Usuários antigos podem usar o email
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Senha</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                required
                data-testid="mobile-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-cyan-600 hover:bg-cyan-700 h-12 text-lg"
            disabled={loading}
            data-testid="mobile-login-submit"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Entrar'}
          </Button>
        </form>

        <p className="text-slate-400 mt-6">
          Não tem conta?{' '}
          <Link to="/m/register" className="text-cyan-400 font-medium">
            Cadastre-se
          </Link>
        </p>
      </div>

      {/* Footer */}
      <div className="p-6 text-center">
        <p className="text-slate-500 text-xs">
          © 2024 G2 Consultoria RH
        </p>
      </div>
    </div>
  );
}
